import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  FormFeedback,
  Label,
  Input,
  Spinner,
  Form,
} from "reactstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { extractFieldErrors } from "common/lib/fieldErrors";
import { resolveContractorDuplicate } from "common/services/contractors.service";
import { toast } from "react-toastify";
import * as Yup from "yup";
import * as moment from "moment";
import { useFormik } from "formik";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useNavigate } from "react-router-dom";

const ResolveContractorDuplicate: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  selectedRecord: any;
  fetchData: () => void;
}> = ({ toggle, isModalOpen, selectedRecord, fetchData }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [, setFieldErrors] = useState<any>([]);
  const navigate = useNavigate();
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      comments: "",
      isConfirmedDuplicate: false,
    },
    validationSchema: Yup.object({
      comments: Yup.string().nullable(),
      isConfirmedDuplicate: Yup.boolean(),
    }),
    onSubmit: async (values: any) => {
      const data = {
        data: {
          note: values.comments,
          status: values.isConfirmedDuplicate ? "duplicate" : "not_duplicate",
        },
      };

      setLoading(true);
      try {
        await resolveContractorDuplicate(selectedRecord?.id, data);
        fetchData();
        validation.resetForm();
        toggle();
        toast.success("Contractor duplicate resolved successfully", {});
      } catch (e: any) {
        const errors = extractFieldErrors(e?.response);
        setFieldErrors(errors);
        toast.error("Contractor duplicate resolution failed", {});
      } finally {
        setLoading(false);
      }
    },
  });

  // Update fieldErrors when validation errors change
  useEffect(() => {
    const validationErrors = [];

    // Convert Formik validation errors to fieldErrors format
    for (const [fieldName, errorMessage] of Object.entries(validation.errors)) {
      if (validation.touched[fieldName] && errorMessage) {
        // Handle nested object errors (like individual.value, mda.value, etc.)
        if (typeof errorMessage === "object" && errorMessage !== null) {
          for (const [nestedField, nestedError] of Object.entries(
            errorMessage
          )) {
            if (nestedError) {
              validationErrors.push({
                field: `${fieldName}.${nestedField}`,
                message: nestedError,
              });
            }
          }
        } else {
          validationErrors.push({
            field: fieldName,
            message: errorMessage,
          });
        }
      }
    }

    setFieldErrors(validationErrors);
  }, [validation.errors, validation.touched]);

  console.log(validation.errors);
  //   console.log(validation.values);
  return (
    <Modal
      id="showModal"
      size="xl"
      isOpen={isModalOpen}
      toggle={toggle}
      centered
    >
      <ModalHeader className="bg-primary-subtle p-3" toggle={toggle}>
        View Contractor Duplicate
      </ModalHeader>

      <Form
        className="resolve-contractor-duplicate-form"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <ModalBody>
          <Row className="mt-3 mb-4">
            <Col sm={6}>
              <h6 className="text-muted text-uppercase fw-semibold mb-3">
                First Contractor Details
              </h6>

              <div
                className="table-responsive border border-dashed rounded p-2  mb-3"
                // style={{ minHeight: "200px" }}
              >
                <table className="table table-md table-borderless table-striped mb-0">
                  <tbody>
                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Contractor Name:
                      </td>
                      <td>
                        <p className="fw-medium mb-0 fs-14" id="billing-name">
                          {`${selectedRecord?.contractorA?.contractorName}` ||
                            "Not Provided"}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Organization Type:
                      </td>
                      <td>
                        {selectedRecord?.contractorA?.organizationType?.name ||
                          "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Acronym:</td>
                      <td>
                        {selectedRecord?.contractorA?.acronym || "Not Provided"}
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Previous or Alias Name:
                      </td>
                      <td>
                        {selectedRecord?.contractorA?.previousOrAliasName
                          ?.map((name: any) => name.name)
                          .join(", ") || "Not Provided"}
                      </td>
                    </tr>

                    <tr>
                      <td className="fw-medium text-muted">SEMGMentions:</td>
                      <td>
                        {selectedRecord?.contractorA?.semgMentions?.count ||
                          "0"}
                      </td>
                    </tr>

                    <tr>
                      <td className="fw-medium text-muted">
                        Performance Ratings:
                      </td>
                      <td>
                        {selectedRecord?.contractorA?.performanceRatings?.some(
                          (rating: any) =>
                            rating.isActive &&
                            (rating.performanceRatingType.isFlag ||
                              rating.performanceRatingType.isDebarred)
                        )
                          ? "Yes"
                          : "No"}
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Contracts:
                      </td>
                      <td>
                        {selectedRecord?.contractorA?.contracts?.count || "0"}
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Assessments:
                      </td>
                      <td>
                        {selectedRecord?.contractorA?.capacityAssessments
                          ?.count +
                          selectedRecord?.contractorA?.riskAssessments?.count +
                          selectedRecord?.contractorA?.pvrs?.count || "0"}
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Last Updated:
                      </td>
                      <td>
                        {moment(
                          new Date(selectedRecord?.contractorA?.updatedAt)
                        ).format("DD MMM Y") || "Not Provided"}
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Created At:
                      </td>
                      <td>
                        {moment(
                          new Date(selectedRecord?.contractorA?.createdAt)
                        ).format("DD MMM Y") || "Not Provided"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>
            <Col sm={6}>
              <h6 className="text-muted text-uppercase fw-semibold mb-3">
                Duplicate Contractor Details
              </h6>

              <div className="table-responsive border border-dashed rounded p-2  mb-3 ">
                <table className="table table-md table-borderless table-striped mb-0">
                  <tbody>
                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "30%" }}
                      >
                        {" "}
                        Contractor Name:
                      </td>
                      <td>
                        {`${selectedRecord?.contractorB?.contractorName}` ||
                          "Not Provided"}
                      </td>
                    </tr>

                    <tr>
                      <td className="fw-medium text-muted">
                        Organization Type:
                      </td>
                      <td>
                        {selectedRecord?.contractorB?.organizationType?.name ||
                          "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Acronym:</td>
                      <td>
                        {selectedRecord?.contractorB?.acronym ||
                          selectedRecord?.contractorB?.contactPersons?.[0]
                            ?.person?.fullName ||
                          "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">
                        Previous or Alias Name:
                      </td>
                      <td>
                        {selectedRecord?.contractorB?.previousOrAliasName
                          ?.map((name: any) => name.name)
                          .join(", ") || "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">SEMGMentions:</td>
                      <td>
                        {selectedRecord?.contractorB?.semgMentions?.count ||
                          "0"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">
                        Performance Ratings:
                      </td>
                      <td>
                        {selectedRecord?.contractorB?.performanceRatings?.some(
                          (rating: any) =>
                            rating.isActive &&
                            (rating.performanceRatingType.isFlag ||
                              rating.performanceRatingType.isDebarred)
                        )
                          ? "Yes"
                          : "No"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Contracts:</td>
                      <td>
                        {selectedRecord?.contractorB?.contracts?.count || "0"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Assessments:</td>
                      <td>
                        {selectedRecord?.contractorB?.capacityAssessments
                          ?.count +
                          selectedRecord?.contractorB?.riskAssessments?.count +
                          selectedRecord?.contractorB?.pvrs?.count || "0"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Last Updated:</td>
                      <td>
                        {moment(
                          new Date(selectedRecord?.contractorB?.updatedAt)
                        ).format("DD MMM Y") || "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Created At:</td>
                      <td>
                        {moment(
                          new Date(selectedRecord?.contractorB?.createdAt)
                        ).format("DD MMM Y") || "Not Provided"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-4">
              <div className="form-check form-switch form-switch-lg px-0 form-check-right">
                <Input
                  name="isConfirmedDuplicate"
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="isConfirmedDuplicate"
                  value={validation.values.isConfirmedDuplicate}
                  onChange={validation.handleChange}
                  checked={validation.values.isConfirmedDuplicate}
                />
                <Label
                  className="form-check-label"
                  htmlFor="isConfirmedDuplicate"
                  style={{ width: "250px" }}
                >
                  Is this a confirmed duplicate contractor?
                </Label>
              </div>
            </Col>
            <Col md={12} className="mb-3">
              <Label htmlFor="id-field" className="form-label">
                Comments on resolution (Optional)
              </Label>
              <CKEditor
                editor={ClassicEditor}
                data={validation.values.comments || ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  validation.setFieldValue("comments", data);
                }}
                onBlur={() => {
                  validation.handleBlur("comments");
                }}
              />
              {validation.touched.comments && validation.errors.comments ? (
                <FormFeedback type="invalid">
                  {/* @ts-ignore */}
                  {validation.errors?.comments}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>
        </ModalBody>

        <div className="modal-footer d-flex justify-content-between">
          <div className="hstack gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                navigate(
                  `/contractors/merge/${selectedRecord?.contractorA?.id}?target=${selectedRecord?.contractorB?.id}`
                );
              }}
            >
              Merge Contractors
            </button>
          </div>
          <div className="hstack gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                toggle();
              }}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading && (
                <Spinner
                  color="light"
                  size="sm"
                  style={{ marginRight: "8px", marginBottom: "-1px" }}
                ></Spinner>
              )}
              Resolve Duplicate
            </button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default ResolveContractorDuplicate;
